import { useEffect } from "react";

function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, key, name] = selector.match(/meta\[(\w+)="([^"]+)"\]/) || [];
    if (key && name) el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function injectJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
}

/**
 * SEO component – updates document title and meta tags dynamically.
 * @param {Object} props
 * @param {string} props.title – Page title (will get " | ANAIZA SHOP" suffix unless `noBrand` is true)
 * @param {string} props.description – Meta description
 * @param {string} [props.image] – OG / Twitter image URL
 * @param {string} [props.path] – Pathname for canonical (defaults to window.location.pathname)
 * @param {Object} [props.jsonLd] – Structured data object (will be serialized as JSON-LD script)
 * @param {boolean} [props.noBrand] – If true, do not append brand suffix
 */
export default function SEO({ title, description, image, path, jsonLd, noBrand = false }) {
  useEffect(() => {
    const fullTitle = noBrand ? title : `${title} | ANAIZA SHOP`;
    document.title = fullTitle;

    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", description);

    if (image) {
      setMeta('meta[property="og:image"]', "content", image);
      setMeta('meta[name="twitter:image"]', "content", image);
    }

    const url = `${window.location.origin}${path || window.location.pathname}`;
    setMeta('meta[property="og:url"]', "content", url);
    setLink("canonical", url);

    injectJsonLd("page-jsonld", jsonLd);
  }, [title, description, image, path, jsonLd, noBrand]);

  return null;
}
